/* eslint-disable react-hooks/exhaustive-deps */
import { LoadingOutlined, EyeOutlined } from '@ant-design/icons';
import { Flex, Image, Typography, type TableProps } from 'antd';
import { ColumnType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatNumberToLocale, formatCurrency, isCurrencyMetric, getFilteredMetrics } from 'utils';

import { Table, TablePagination } from 'components/common';

import { useConstantsQuery } from 'modules/apiData/dataApiSlice';
import { useOrganization } from 'modules/organization/useOrganization';
import { usePerformance } from 'modules/performance/usePerformance';

import { authStorage } from '../../modules/auth/authStorage';
import { Metric } from '../../modules/misc/miscApiTypes';
import { DSP_AMAZON } from '../../utils/constants';
import { AdPreviewModal } from './AdPreviewModal';
import { UNKNOWN_DIMENSION_NAMES, VIDEO_PER_IMPRESSION_METRICS } from './constants';

type DataType = Record<string, unknown>;

const { Text } = Typography;

const PIXEL_DIMENSIONS = ['PIXEL_CATEGORY', 'PIXEL_SUBCATEGORY'];
const isPixelDimension = (dimension?: string | null): boolean =>
  !!dimension && PIXEL_DIMENSIONS.includes(dimension);
const isEmptyValue = (value: unknown): boolean =>
  value === undefined || value === null || value === '';

// Metrics that always default to 0 on pixel-category rows (derived from impressions/spend, which
// live only on the uncategorized row). Hidden when grouping by a pixel dimension; replaced by Counts.
const PIXEL_HIDDEN_METRICS = [
  'IMPRESSIONS',
  'CLICKS',
  'CTR',
  'CPC',
  'CPM',
  'SPEND',
  'FREQUENCY',
  'VCR',
  'PCT_25',
  'PCT_50',
  'PCT_75',
  'PCT_100',
  'ROAS',
  'CPA_CHECKOUTS',
  'CPA_LEADS',
  'CPA_PAGE_VISITS',
  'CPA_ACTIVITY',
];

const flattenNestedData = (data: DataType[]): DataType[] => {
  const flattenedData: DataType[] = [];

  data.forEach((row) => {
    const { NESTED: nestedData, ...rest } = row;

    flattenedData.push({ ...rest, FIRST_DIMENSION: true });
    if (nestedData) {
      flattenedData.push(...(nestedData as DataType[]));
    }
  });

  return flattenedData;
};

export const AdvancedAnalyticsTable: React.FC = () => {
  const {
    selectedView,
    views,
    widgets,
    analytics,
    selectedDimensions,
    isGridLoading,
    analyticsPage,
    pickAnalyticsPage,
    analyticsPageSize,
    pickAnalyticsPageSize,
    pickAnalyticsOrder,
    pickAnalyticsOrderBy,
    selectedColumns,
  } = usePerformance();
  const { currentAdvertiser, dsp } = useOrganization();

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewCreativeId, setPreviewCreativeId] = useState<string | null>(null);

  const openPreview = (creativeId: string) => {
    const cleanCreativeId = creativeId?.split(',')[0];

    // For other DSPs, use the modal
    setPreviewCreativeId(cleanCreativeId);
    setPreviewVisible(true);
  };
  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewCreativeId(null);
  };

  const [data, setData] = useState<DataType[]>();
  const [columns, setColumns] = useState<ColumnType<DataType>[]>([]);
  const { data: constants } = useConstantsQuery(null);
  const metricsDisplay = useMemo(() => (constants && constants.Metrics) || {}, [constants]);
  const dimensionsDisplay = useMemo(() => (constants && constants.Dimensions) || {}, [constants]);
  const [authToken, setAuthToken] = useState<string | null>(null);

  useEffect(() => {
    const fetchAuthToken = async () => {
      const token = await authStorage.getIdToken();

      setAuthToken(token);
    };

    fetchAuthToken();
  }, []);

  const renderFirstColumn = useCallback(
    (text: string, record: DataType) => {
      const firstDim = selectedDimensions[0];
      const secondDim = selectedDimensions[1];
      const isFirstCreative = firstDim === 'CREATIVE';
      const isSecondCreative = secondDim === 'CREATIVE';
      const pixelMode = isPixelDimension(firstDim) || isPixelDimension(secondDim);

      const icon = record.ICON ? (
        <Image
          src={record.ICON as string}
          preview={false}
          className="rounded w-10"
          onError={(e) => {
            const imgElement = e.target as HTMLImageElement;

            if (!imgElement.dataset.fallback) {
              imgElement.dataset.fallback = 'true';
              imgElement.src =
                process.env.REACT_APP_BACKEND_API_URL +
                  '/image/publisher_icon?key=unknownpublisher.png&idToken=' +
                  authToken || '';
            }
          }}
        />
      ) : null;
      let textDisplay = UNKNOWN_DIMENSION_NAMES[text] || text;

      const isNestedRow = !record.FIRST_DIMENSION;

      if (isNestedRow && secondDim && record[secondDim]) {
        const secondDimensionName = record[secondDim] as string;

        textDisplay = UNKNOWN_DIMENSION_NAMES[secondDimensionName] || secondDimensionName;
      }

      // For pixel dimensions the uncategorized bucket has a null value; show "N/A" instead of "-".
      if (pixelMode && !record.TOTAL_ROW) {
        const rawValue = isNestedRow ? (secondDim ? record[secondDim] : undefined) : text;

        textDisplay = isEmptyValue(rawValue)
          ? 'N/A'
          : UNKNOWN_DIMENSION_NAMES[rawValue as string] || (rawValue as string);
      }

      const canPreview = (isFirstCreative && !isNestedRow) || (isSecondCreative && isNestedRow);
      const showPreview = canPreview && !record.TOTAL_ROW && dsp !== DSP_AMAZON;

      return (
        <Flex align="center" gap={10}>
          <div className="min-w-10"> {icon}</div>
          <Flex vertical>
            {showPreview ? (
              <>
                <Text
                  className="ctv-creative-text"
                  onClick={() => openPreview(record['CREATIVE_ID'] as string)}
                >
                  {`${textDisplay || '-'}`}
                </Text>
                <Flex align="center" gap={4} className="mt-1">
                  <EyeOutlined className="text-primary-gray-400 text-xs" />
                  <Text
                    className="text-primary-gray-400 text-xs cursor-pointer"
                    onClick={() => openPreview(record['CREATIVE_ID'] as string)}
                  >
                    Preview
                  </Text>
                </Flex>
              </>
            ) : (
              <Text>{`${textDisplay || '-'}`}</Text>
            )}
          </Flex>
        </Flex>
      );
    },
    [selectedDimensions, dsp],
  );

  const getFirstColumn = useCallback((): ColumnType<DataType> => {
    if (!selectedDimensions[0]) {
      return {};
    }

    const firstDimensionName =
      (selectedDimensions[0] && dimensionsDisplay[selectedDimensions[0]]?.shownName) ||
      selectedDimensions[0];
    const secondDimensionName =
      (selectedDimensions[1] && dimensionsDisplay[selectedDimensions[1]]?.shownName) ||
      selectedDimensions[1];
    const title = [firstDimensionName, secondDimensionName].filter(Boolean).join(' / ');

    return {
      title,
      dataIndex: selectedDimensions[0],
      sorter: true,
      fixed: 'left',
      className: 'w-96 min-w-96 border-r border-primary-gray-100',
      render: renderFirstColumn,
    };
  }, [selectedDimensions, dimensionsDisplay]);

  const getMetrics = useCallback((): ColumnType<DataType>[] => {
    const viewMetrics = views?.find((view) => view.viewId === selectedView)?.metrics || [];
    const metricsList = (selectedColumns?.length && selectedColumns) || viewMetrics;

    // If selectedColumns exists, use its order (preserves user's custom order from sortable list)
    // Otherwise, sort viewMetrics to match allMetrics order (same as EditColumnsModal checkboxes)
    let sortedMetrics = metricsList;

    if (!selectedColumns?.length && constants) {
      const allMetrics = getFilteredMetrics<Metric>(constants, dsp);
      const allMetricsOrder = Object.values(allMetrics).map((m) => m.value);

      sortedMetrics = [...metricsList].sort(
        (a, b) => allMetricsOrder.indexOf(a) - allMetricsOrder.indexOf(b),
      );
    }

    let metrics = sortedMetrics.map((metric) => ({
      title: metricsDisplay[metric]?.shownName || metric,
      dataIndex: metric,
      sorter: true,
      render: (value: string) => value || '-',
    }));

    // For pixel dimensions, hide the metrics that always default to 0 on category rows and
    // add a conversion Counts column instead.
    if (isPixelDimension(selectedDimensions[0]) || isPixelDimension(selectedDimensions[1])) {
      metrics = metrics.filter(
        (metric) => !PIXEL_HIDDEN_METRICS.includes(metric.dataIndex as string),
      );
      metrics.push({
        title: 'Counts',
        dataIndex: 'COUNTS',
        sorter: true,
        render: (value: string) => value || '-',
      });
    }

    return metrics;
  }, [selectedView, views, metricsDisplay, selectedColumns, constants, dsp, selectedDimensions]);

  const getTotalRow = useCallback(
    (data: DataType[], columns: ColumnType<DataType>[]) => {
      const totalRow: Record<string, unknown> = {};

      columns.forEach((column) => {
        const dataIndex = column.dataIndex as string;

        totalRow[dataIndex] = widgets?.current?.[dataIndex];
      });

      if (selectedDimensions[0]) {
        totalRow[selectedDimensions[0] as string] = 'Total';
      }

      totalRow.TOTAL_ROW = true;

      return totalRow;
    },
    [selectedDimensions, widgets],
  );

  const formatData = useCallback(
    (data: DataType[]) =>
      data
        .map((row) => {
          const formattedRow = { ...row };
          const hasZeroImpressions = row.IMPRESSIONS === 0;

          Object.keys(row).forEach((key) => {
            if (hasZeroImpressions && VIDEO_PER_IMPRESSION_METRICS.has(key)) {
              formattedRow[key] = 'N/A';

              return;
            }
            if (typeof row[key] === 'number') {
              if (isCurrencyMetric(key)) {
                formattedRow[key] = formatCurrency(
                  row[key] as number,
                  currentAdvertiser?.currency || 'USD',
                );
              } else {
                formattedRow[key] = formatNumberToLocale(row[key] as number, {
                  suffix: metricsDisplay[key]?.unit === '%' ? '%' : undefined,
                });
              }
            }
          });

          return formattedRow;
        })
        .map((row, i) => ({ ...row, key: i })),
    [metricsDisplay, currentAdvertiser],
  );

  useEffect(() => {
    const firstColumn = getFirstColumn();
    const metrics = getMetrics();

    setColumns([firstColumn, ...metrics]);
  }, [getFirstColumn, getMetrics]);

  useEffect(() => {
    if (columns && columns.length > 1 && analytics?.data && widgets?.current) {
      const data = flattenNestedData(analytics.data);
      const totalRow = getTotalRow(data, columns);

      data.push(totalRow);
      const formattedData = formatData(data);

      setData(formattedData);
    } else {
      setData([]);
    }
  }, [columns, analytics]);

  useEffect(() => {
    pickAnalyticsPage(0);
  }, [selectedDimensions]);

  const handleTableChange: TableProps<DataType>['onChange'] = (_pagination, _filters, sorter) => {
    const sorterOrder = Array.isArray(sorter) || !sorter?.order ? null : sorter.order;
    const sorterField = Array.isArray(sorter) || !sorter?.field ? null : sorter.field;
    const field = Array.isArray(sorterField) || !sorterField ? null : (sorterField as string);

    const order = (sorterOrder && (sorterOrder === 'descend' ? 'DESC' : 'ASC')) || null;
    const orderBy = order && field && !selectedDimensions.includes(field) ? field : null;

    pickAnalyticsOrder(order);
    pickAnalyticsOrderBy(orderBy);
  };

  return (
    <div className="px-3 pt-3 pb-0 grid w-full">
      <Table
        className="ctv-analytics-table overflow-x-auto overflow-y-hidden p-0"
        columns={columns}
        dataSource={data}
        pagination={false}
        loading={isGridLoading && { indicator: <LoadingOutlined spin /> }}
        onChange={handleTableChange}
        rowClassName={(record) => {
          if (record.TOTAL_ROW) {
            return 'total-row';
          }
          if (record.FIRST_DIMENSION) {
            return 'first-dimension-row';
          }

          return 'second-dimension-row';
        }}
      />
      <TablePagination
        totalPages={analytics?.totalPages || 0}
        totalCount={analytics?.totalCount}
        pageSize={analyticsPageSize}
        setPageSize={pickAnalyticsPageSize}
        page={analyticsPage}
        setPage={pickAnalyticsPage}
      />

      <AdPreviewModal
        creativeId={previewCreativeId!}
        isOpen={previewVisible}
        onClose={closePreview}
      />
    </div>
  );
};
